import React, { memo, useState, useEffect } from "react";
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { getFirestore, collection, where, onSnapshot, query } from "firebase/firestore";
import { useSession } from "../firebase/UserProvider";
import { createUserDocument } from "../firebase/Users";
import logo from "../img/BMSLogo.jpg";

export default memo(function Login(props) {
  const [invalidEmail, setInvalidEmail] = useState(false);
  const { user } = useSession();
  const [showMaintenenceMessage, setShowMaintenenceMessage] = useState(false);
  const [maintenenceMessage, setMaintenenceMessage] = useState("");

  const signIn = () => {
    const provider = new GoogleAuthProvider();
    const auth = getAuth();

    provider.setCustomParameters({ hd: "bmspune.org" });

    signInWithPopup(auth, provider)
      .then(async (result) => {
        var loggedInUser = result.user;
        const userEmail = loggedInUser.email;
        if (userEmail.split("@")[1] !== "bmspune.org") {
          setInvalidEmail(true);
          loggedInUser.delete();
        } else {
          createUserDocument(loggedInUser);
          const redirectPath = sessionStorage.getItem("redirectPath") || "/home";
          sessionStorage.removeItem("redirectPath");
          props.history.push(redirectPath);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }; //end of signIn

  useEffect(() => {
    const db = getFirestore();
    const systemPreferencesCollection = collection(db, "system-preferences");
    const q = query(
      systemPreferencesCollection,
      where("name", "==", "showMaintenanceMessage")
    );
    onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        let value = snapshot.docs[0].data().value;
        let message = snapshot.docs[0].data().message;

        setShowMaintenenceMessage(value);
        setMaintenenceMessage(message);
      }
    });
    if (user) {
      const redirectPath = sessionStorage.getItem("redirectPath") || "/home";
      sessionStorage.removeItem("redirectPath");
      props.history.push(redirectPath);
    }
  }, [user, props.history]);

  return (
    <div
      id="login-page-root"
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "var(--theme-color-canvas)",
        padding: "var(--theme-spacing-xl)",
        position: "relative",
      }}
    >
      {/* Maintenance Mode Banner */}
      {showMaintenenceMessage && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            backgroundColor: "#B71C1C",
            color: "#FFFFFF",
            padding: "var(--theme-spacing-md)",
            textAlign: "center",
            fontWeight: "bold",
            zIndex: 1000,
            fontFamily: "var(--theme-font-family-body)",
            fontSize: "14px",
          }}
        >
          ⚠️ {maintenenceMessage}
        </div>
      )}

      {/* Centered Login Card */}
      <div
        id="login-card"
        style={{
          width: "100%",
          maxWidth: "400px",
          backgroundColor: "var(--theme-card-bg)",
          borderRadius: "var(--theme-card-radius, var(--theme-rounded-md))",
          padding: "var(--theme-spacing-3xl) var(--theme-spacing-2xl)",
          boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
          border: "1px solid var(--theme-color-mute)",
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          marginTop: showMaintenenceMessage ? "60px" : "0px",
          transition: "margin-top 0.3s ease",
        }}
      >
        {/* School Logo */}
        <img
          src={logo}
          alt="Bhaktivedanta Model School logo"
          style={{
            maxHeight: "72px",
            display: "block",
            margin: "0 auto var(--theme-spacing-xl)",
            objectFit: "contain",
          }}
        />

        {/* App Title */}
        <h2
          style={{
            fontFamily: "var(--theme-font-family-display)",
            fontSize: "var(--theme-type-display-xs-font-size, 20px)",
            fontWeight: "var(--theme-type-display-xs-font-weight, 600)",
            lineHeight: "var(--theme-type-display-xs-line-height, 24px)",
            textAlign: "center",
            color: "var(--theme-color-ink)",
            marginBottom: "var(--theme-spacing-sm)",
          }}
        >
          {process.env.REACT_APP_APP_TITLE || "BMS Intranet"}
        </h2>

        {/* Subtitle */}
        <p
          style={{
            fontFamily: "var(--theme-font-family-body)",
            fontSize: "var(--theme-type-caption-font-size, 12px)",
            fontWeight: "var(--theme-type-caption-font-weight, 400)",
            lineHeight: "var(--theme-type-caption-line-height, 16px)",
            textAlign: "center",
            color: "var(--theme-color-body)",
            marginBottom: "var(--theme-spacing-2xl)",
          }}
        >
          Sign in with your @bmspune.org account
        </p>

        {/* Sign-in Button */}
        {!user ? (
          <button
            onClick={signIn}
            disabled={showMaintenenceMessage}
            aria-label="Sign in with Google"
            style={{
              width: "100%",
              backgroundColor: "var(--theme-button-primary-bg)",
              color: "var(--theme-button-primary-text)",
              border: "none",
              borderRadius: "var(--theme-rounded-md)",
              padding: "var(--theme-spacing-md) var(--theme-spacing-xl)",
              fontFamily: "var(--theme-font-family-body)",
              fontSize: "var(--theme-type-button-md-font-size, 16px)",
              fontWeight: "var(--theme-type-button-md-font-weight, 600)",
              lineHeight: "var(--theme-type-button-md-line-height, 24px)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: showMaintenenceMessage ? "not-allowed" : "pointer",
              transition: "opacity 0.2s ease-in-out",
              opacity: showMaintenenceMessage ? 0.6 : 1,
            }}
            onMouseOver={(e) => {
              if (!showMaintenenceMessage) e.currentTarget.style.opacity = "0.9";
            }}
            onMouseOut={(e) => {
              if (!showMaintenenceMessage) e.currentTarget.style.opacity = "1";
            }}
          >
            <img
              src={logo}
              alt="BMS Logo"
              style={{
                width: "20px",
                height: "20px",
                marginRight: "var(--theme-spacing-sm)",
                borderRadius: "50%",
                objectFit: "contain",
                backgroundColor: "#fff",
                padding: "2px"
              }}
            />
            Access BMS Intranet
          </button>
        ) : (
          <div
            style={{
              textAlign: "center",
              fontFamily: "var(--theme-font-family-body)",
              fontSize: "var(--theme-type-body-sm-font-size, 14px)",
              color: "var(--theme-color-ink)",
              padding: "var(--theme-spacing-md)",
            }}
          >
            You have already logged in as <strong>{user.displayName}</strong> with email as <strong>{user.email}</strong>.
          </div>
        )}

        {/* Invalid Email domain error */}
        {invalidEmail && (
          <div
            style={{
              marginTop: "var(--theme-spacing-lg)",
              padding: "var(--theme-spacing-sm) var(--theme-spacing-md)",
              backgroundColor: "#fff5f5",
              border: "1px solid #feb2b2",
              borderRadius: "var(--theme-rounded-sm)",
              color: "var(--theme-color-error, #c53030)",
              fontSize: "var(--theme-type-caption-font-size, 12px)",
              fontWeight: "600",
              textAlign: "center",
              fontFamily: "var(--theme-font-family-body)",
            }}
          >
            The email domain must be @bmspune.org. Please sign in with authorized credentials.
          </div>
        )}
      </div>
    </div>
  );
});
